'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';

import { appConfig } from '@/config/appConfig';

type UseI18nReturn = {
    t: (key: string, values?: Record<string, string | number | Date>) => string;
    locale: string;
    locales: string[];
    isArabic: boolean;
    isRTL: boolean;
    direction: 'ltr' | 'rtl';
    changeLocale: (locale: string) => void;
};

export default function useI18n(): UseI18nReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const t = useTranslations();
    const locale = useLocale();
    const { locales } = appConfig.i18n;
    const isArabic = locale === 'ar';
    const isRTL = isArabic;
    const direction = isRTL ? 'rtl' : 'ltr';

    // ----------------------------------------------------------------------------------------------------
    // MARK: Functions
    // ----------------------------------------------------------------------------------------------------
    function changeLocale(locale: string): void {
        const pathnameParts = pathname.split('/');
        pathnameParts[1] = locale;
        const newPathname = pathnameParts.join('/');
        const params = new URLSearchParams(searchParams);
        router.push(`${newPathname}${params.toString() ? `?${params.toString()}` : ''}`);
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        t,
        locale,
        locales,
        isArabic,
        isRTL,
        direction,
        changeLocale,
    };
}
